import { isBilling2023Subscription } from '@mm/state/model/SubscriptionModel';
import { Dispatch } from 'redux';
import { UNAUTHORIZED, UNPROCESSABLE_ENTITY } from '@mm/constants/status-codes';
import { getErrorJson } from '@mm/utils';
import SubscriptionController, {
  CouponObject,
  PlansResponse,
} from '../controllers/SubscriptionsController';
import commonHandler from '../errorHandlers/common-handler';
import { planInfoHandler } from '../errorHandlers/plan-info-handler';
import { subscriptionErrorHandler } from '../errorHandlers/subscriptions-handler';
import {
  EnhancedPlan,
  Plan,
  PlansAllotments,
  PlansMapping,
  PlansPrices,
  SubscriptionResponse,
  UserSeatsMapping,
} from '../interfaces/subscription';
import { OutgoingMessagesStatistic } from '../model/ConnectedPageModel';
import { SubscriptionModel } from '../model/SubscriptionModel';
import modalActions from '../modules/modalsState/actions';
import {
  selectActiveCompanyId,
  selectIsShopifyContext,
} from '../selectors/viewSelectors';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import companyCRUDActions from './CompanyCRUDActions';
import connectedPageCRUDActions from './ConnectedPageCRUDActions';
import CrudActionContainer from './CrudActionContainer';
import twilioCRUDActions from './TwilioCRUDActions';
import ShopifyController from '../controllers/ShopifyController';
import reduxStore from '../store';

export type UpgradePayload = {
  facebookPageId?: number;
  planCode?: string;
  coupon?: string;
  recurlyToken?: string;
  companyId: number;
  landmineOpenId?: number;
};

export type CreateProSubscriptionPayload = {
  companyId: number;
  planCode: string;
  facebookPageId?: number;
  recurlyToken?: string | null;
  coupon?: string | null;
  landmineOpenId?: number;
  utmSid?: string;
};

export type CreateTeamSubscriptionPayload = {
  companyId: number;
  planCode: string;
  facebookPageId?: number;
  coupon?: string | null;
  recurlyToken?: string | null;
  landmineOpenId?: number;
  utmSid?: string;
};

export type DowngradeOrCancelPayload = {
  facebookPageId?: number;
  planCode?: string;
  coupon?: string;
  recurlyToken?: string;
  leavingReason?: string;
  additionalInfo?: string;
  email?: string;
  companyId: number;
  pollResults: {};
};

export type AttachPhoneNumberPayload = {
  subscriptionId: number;
  smsNumber: string;
  facebookPageId: number;
  companyId: number;
  skipFetchActivePage?: boolean;
};

export type BillingInfo = {
  address1: string | null;
  address2: string | null;
  cardType: string;
  city: string;
  country: string;
  zip: string;
};

type CreateSubscriptionPayload =
  | CreateProSubscriptionPayload
  | CreateTeamSubscriptionPayload;
type SwappedPageStatistics = {
  currentOutgoingMessagesStatistic: OutgoingMessagesStatistic;
  outgoingMessagesLimit: number;
};

class SubscriptionCRUDActions extends CrudActionContainer {
  setAccountDetailsAction: string;
  clearAccountDetailsAction: string;
  clearCurrentSubscriptionAction: string;
  setPlansAction: string;
  planInfoAction: string;
  setOveragesEnabledAction: string;
  swappedPagesAction: string;

  setPriceForNextLevelAction: string;
  setCurrentSubscriptionAction: string;
  setCreditsForFuturePaymentsAction: string;

  constructor() {
    super('SUBSCRIPTION');
    this.setAccountDetailsAction = super.makeActionKey('SET_ACCOUNT_DETAILS');
    this.clearAccountDetailsAction = super.makeActionKey(
      'CLEAR_ACCOUNT_DETAILS',
    );
    this.clearCurrentSubscriptionAction = super.makeActionKey(
      'CLEAR_CURRENT_SUBSCRIPTION',
    );
    this.planInfoAction = super.makeActionKey('PLAN_INFO');
    this.setPlansAction = super.makeActionKey('SET_PLANS');
    this.setOveragesEnabledAction = super.makeActionKey('SET_OVERAGES_ENABLED');
    this.setPriceForNextLevelAction = super.makeActionKey(
      'SET_PRICE_FOR_NEXT_LEVEL',
    );
    this.swappedPagesAction = super.makeActionKey('SWAPPED_PAGES');
    this.setCurrentSubscriptionAction = super.makeActionKey(
      'SET_CURRENT_SUBSCRIPTION',
    );
    this.setCreditsForFuturePaymentsAction = super.makeActionKey(
      'SET_CREDITS_FOR_FUTURE_PAYMENTS',
    );
  }

  getPlansMainApp(companyId: number, skipRedux = false) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await SubscriptionController.getPlans(companyId);
        if (!skipRedux) {
          dispatch(
            this.setPlansActionCreator({
              ...response,
              planAllotments: response.plansAllotments,
              annualPlanAllotments: response.annualPlansAllotments,
            }),
          );
        }
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  getShopifyPlans(skipRedux = false) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await ShopifyController.getShopifyPlans();
        if (!skipRedux) {
          dispatch(this.setPlansActionCreator(response));
        }
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  getPlans(companyId: number, skipRedux = false) {
    const state = reduxStore.getState();
    const isShopify = selectIsShopifyContext(state);
    if (isShopify) {
      return this.getShopifyPlans() as () => Promise<PlansResponse | undefined>;
    }

    return this.getPlansMainApp(companyId, skipRedux);
  }

  getPlan(planId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await SubscriptionController.getPlan(planId);
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  getSubscription(subscriptionId: number, pageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.setCurrentSubscriptionAction));
        const response = await SubscriptionController.getSubscription(
          subscriptionId,
        );
        dispatch(this.unbusy());
        dispatch(this.setCurrentSubscriptionActionCreator(pageId, response));
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  getPlanInfo(planCode: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await SubscriptionController.planInfo(planCode);
        dispatch(
          this.setPlanInfoActionCreator({
            plan: response.plan,
          }),
        );
        return response;
      } catch (e) {
        planInfoHandler(dispatch)(e);
      }
    };
  }

  createProSubscription(
    payload: CreateProSubscriptionPayload,
    plan: EnhancedPlan,
  ) {
    return async (dispatch: Dispatch<StoreState>) =>
      dispatch(this.createSubscription(payload, plan));
  }

  createTeamSubscription(
    payload: CreateTeamSubscriptionPayload,
    plan: EnhancedPlan,
  ) {
    return this.createSubscription(payload, plan);
  }

  updateAllotment(payload: {
    subscriptionId: number;
    level: number;
    companyId: number;
    facebookPageId: number;
    loadContactsCount?: boolean;
    fetchActivePage?: boolean;
  }) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await SubscriptionController.upgradeAllotment(payload);

        dispatch(this.getAccountData(payload.companyId));
        dispatch(this.getCurrentSubscription(payload.facebookPageId, false));
        await dispatch(
          connectedPageCRUDActions.retrieveBasicListByCompany(
            payload.companyId,
            {
              withSubscription: true,
              withDowngradePending: true,
              withMessagesStatistic: true,
              withContactsCount: payload.loadContactsCount || false,
            },
          ),
        );
        if (payload.facebookPageId && payload.fetchActivePage) {
          await dispatch(
            connectedPageCRUDActions.retrievePage(payload.facebookPageId),
          );
        }
        return response;
      } catch (e) {
        return subscriptionErrorHandler(dispatch)(e);
      }
    };
  }

  getPriceForNextLevel(payload: { subscriptionId: number; nextLevel: number }) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy());
        const allotment = await SubscriptionController.getPriceForNextLevel(
          payload,
        );
        dispatch(this.setPriceForNextLevelActionCreator(allotment));
        dispatch(this.unbusy());
        return allotment;
      } catch (e) {
        if (e.status === 422) {
          dispatch(this.unbusy());
          return;
        }
        dispatch(this.unbusy());
        return subscriptionErrorHandler(dispatch)(e);
      }
    };
  }

  sendOveragesEnabled(
    subscriptionId: number,
    overagesEnabled: boolean,
    facebookPageId: number,
    skipFetchActivePage?: boolean,
    companyIdOverride?: number,
    loadContactsCount?: boolean,
    useV2Route?: boolean,
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      try {
        dispatch(this.busy(this.updateAction));
        const state = getState();
        const companyId = companyIdOverride || selectActiveCompanyId(state);
        const response = await SubscriptionController.sendOveragesEnabled(
          subscriptionId,
          overagesEnabled,
          facebookPageId,
          useV2Route,
        );

        if (facebookPageId && !skipFetchActivePage) {
          dispatch(connectedPageCRUDActions.retrievePage(facebookPageId));
        }
        dispatch(
          connectedPageCRUDActions.retrieveBasicListByCompany(companyId, {
            withSubscription: true,
            withDowngradePending: true,
            withMessagesStatistic: true,
            withContactsCount: loadContactsCount || false,
          }),
        );
        dispatch(
          this.setOveragesEnabledActionCreator(
            subscriptionId,
            response.overagesEnabled,
            facebookPageId,
          ),
        );
        dispatch(this.unbusy());
        return response;
      } catch (e) {
        return subscriptionErrorHandler(dispatch)(e);
      }
    };
  }

  update(
    subscriptionId: number,
    payload: Pick<SubscriptionModel, 'xrayLimit'>,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.updateAction));
        const response = await SubscriptionController.updateSubscription(
          subscriptionId,
          payload,
        );
        dispatch(this.unbusy());
        return response;
      } catch (e) {
        subscriptionErrorHandler(dispatch)(e);
        throw e;
      }
    };
  }

  upgrade(subscriptionId: number, payload: UpgradePayload, plan: EnhancedPlan) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await SubscriptionController.upgrade(
          subscriptionId,
          payload,
          plan,
        );

        dispatch(companyCRUDActions.retrieveAll());
        dispatch(this.unbusy());
        dispatch(this.getAccountData(payload.companyId));

        return response;
      } catch (e) {
        return subscriptionErrorHandler(dispatch)(e);
      }
    };
  }

  cancelEmail(
    companyId: number,
    subscriptionId: number,
    payload: DowngradeOrCancelPayload,
    pageNumber: number,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await SubscriptionController.cancel(
          subscriptionId,
          payload,
        );
        dispatch(companyCRUDActions.retrieveAll());
        dispatch(this.getAccountData(companyId));
        dispatch(this.unbusy());
        return response;
      } catch (e) {
        return subscriptionErrorHandler(dispatch)(e);
      }
    };
  }

  getAccountData(companyId: number, signal?: AbortSignal) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.retrieveAllAction));
        const accountDetails = await SubscriptionController.getAccountData(
          companyId,
          signal,
        );
        dispatch(this.setAccountDetailsActionCreator(accountDetails));
        return accountDetails;
      } catch (e) {
        if (e.status === UNAUTHORIZED) {
          dispatch(this.setAccountDetailsActionCreator(undefined));
          return undefined;
        }
        return commonHandler(dispatch)(e);
      }
    };
  }

  getCouponData(coupon: string, planCode: string, companyId: number) {
    return async (
      dispatch: Dispatch<StoreState>,
    ): Promise<CouponObject | undefined> => {
      try {
        dispatch(this.busy(this.retrieveAllAction));
        const response = await SubscriptionController.getCouponData(
          coupon,
          planCode,
          companyId,
        );
        dispatch(this.unbusy());
        return response.coupon;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  activate(subscriptionId: number, companyId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await SubscriptionController.activate(subscriptionId);
        dispatch(companyCRUDActions.retrieveAll());
        dispatch(this.unbusy());
        dispatch(this.getAccountData(companyId));

        return response;
      } catch (e) {
        return subscriptionErrorHandler(dispatch)(e);
      }
    };
  }

  downgrade(
    companyId: number,
    subscriptionId: number,
    payload: DowngradeOrCancelPayload,
    loadContactsCount?: boolean,
    useV2Route?: boolean,
    isCancel?: boolean,
    plan?: EnhancedPlan,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await SubscriptionController.downgrade(
          subscriptionId,
          payload,
          useV2Route,
          isCancel,
          plan,
        );
        dispatch(
          connectedPageCRUDActions.retrieveBasicListByCompany(companyId, {
            withSubscription: true,
            withDowngradePending: true,
            withMessagesStatistic: true,
            withContactsCount: loadContactsCount || false,
          }),
        );
        dispatch(companyCRUDActions.retrieveAll());
        dispatch(this.getAccountData(companyId));
        if (payload.facebookPageId) {
          dispatch(
            connectedPageCRUDActions.retrievePage(payload.facebookPageId),
          );
          dispatch(
            this.getCurrentSubscription(
              payload.facebookPageId,
              false,
              response?.newSubscription?.id,
              isBilling2023Subscription(response.newSubscription),
            ),
          );
        }
        dispatch(this.unbusy());
        return response;
      } catch (e) {
        dispatch(this.unbusy());
        return subscriptionErrorHandler(dispatch)(e);
      }
    };
  }

  clearAccountData() {
    return {
      type: this.clearAccountDetailsAction,
    };
  }

  clearCurrentSubscription() {
    return {
      type: this.clearCurrentSubscriptionAction,
    };
  }

  addExtraPagesToTeam(
    subscriptionId: number,
    companyId: number,
    pagesCount: number,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await SubscriptionController.addExtraPagesToTeam(
          subscriptionId,
          pagesCount,
        );
        dispatch(this.getAccountData(companyId));

        return response;
      } catch (e) {
        return subscriptionErrorHandler(dispatch)(e);
      }
    };
  }

  swapPages(
    subscription: SubscriptionModel,
    facebookPageId: number,
    newPageId: number,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await SubscriptionController.swapPages(
          subscription.id,
          facebookPageId,
          newPageId,
        );
        dispatch(
          this.swappedPagesActionCreator(
            subscription,
            facebookPageId,
            newPageId,
            response.oldPageStatistics,
            response.newPageStatistics,
          ),
        );
        return response;
      } catch (e) {
        return subscriptionErrorHandler(dispatch)(e);
      }
    };
  }

  swappedPagesActionCreator = (
    subscription: SubscriptionModel,
    facebookPageId: number,
    newPageId: number,
    oldPageStatistics: SwappedPageStatistics,
    newPageStatistics: SwappedPageStatistics,
  ) => ({
    type: this.swappedPagesAction,
    data: {
      subscription,
      facebookPageId,
      newPageId,
      oldPageStatistics,
      newPageStatistics,
    },
  });

  setPlanInfoActionCreator = ({ plan }: { plan: Plan }) => ({
    type: this.planInfoAction,
    plan,
  });

  setPlansActionCreator = ({
    plans,
    plansMapping,
    plansPrices,
    specialDeal,
    offerTeam,
    planAllotments,
    annualPlanAllotments,
    plansUserSeats,
  }: {
    plans: Plan[];
    plansMapping?: PlansMapping;
    plansPrices?: PlansPrices;
    specialDeal?: boolean;
    offerTeam?: boolean;
    planAllotments?: PlansAllotments;
    annualPlanAllotments?: PlansAllotments;
    plansUserSeats?: UserSeatsMapping;
  }) => ({
    type: this.setPlansAction,
    plans,
    plansMapping,
    plansPrices,
    specialDeal,
    offerTeam,
    planAllotments,
    annualPlanAllotments,
    plansUserSeats,
  });

  setAccountDetailsActionCreator = (accountDetails?: SubscriptionResponse) => ({
    type: this.setAccountDetailsAction,
    accountDetails,
  });

  setOveragesEnabledActionCreator = (
    subscriptionId: number,
    overagesEnabled: boolean,
    facebookPageId?: number,
  ) => ({
    type: this.setOveragesEnabledAction,
    data: { facebookPageId, overagesEnabled, subscriptionId },
  });

  setPriceForNextLevelActionCreator = priceForNextLevel => ({
    type: this.setPriceForNextLevelAction,
    data: { priceForNextLevel },
  });

  setCurrentSubscriptionActionCreator = (
    pageId: number,
    subscription: SubscriptionModel,
  ) => ({
    type: this.setCurrentSubscriptionAction,
    data: { pageId, subscription },
  });

  setCreditsForFuturePaymentsActionCreator = ({
    futurePaymentCredits,
  }: {
    futurePaymentCredits: number;
  }) => ({
    type: this.setCreditsForFuturePaymentsAction,
    data: { futurePaymentCredits },
  });

  attachSMSNumber(payload: AttachPhoneNumberPayload) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await SubscriptionController.attachSMSNumber(payload);

        dispatch(companyCRUDActions.retrieveAll());
        dispatch(
          twilioCRUDActions.activatePage(payload.facebookPageId, false, {
            numberKind: 'twilio',
            number: payload.smsNumber,
            skipFetchActivePage: payload.skipFetchActivePage,
          }),
        );
        dispatch(this.getAccountData(payload.companyId));

        return response;
      } catch (e) {
        return subscriptionErrorHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  private createSubscription(
    payload: CreateSubscriptionPayload,
    plan: EnhancedPlan,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.createAction));
        const response = await SubscriptionController.postSubscription(
          payload,
          plan,
        );

        dispatch(companyCRUDActions.retrieveAll());

        dispatch(this.getAccountData(payload.companyId));

        return response;
      } catch (e) {
        return subscriptionErrorHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  getCurrentSubscription(
    pageId: number,
    bySubscription: boolean,
    subscriptionId?: number,
    isNewBilling2023Subscription?: boolean,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy(this.setCurrentSubscriptionAction));
        let response;
        if (subscriptionId && isNewBilling2023Subscription) {
          response = await SubscriptionController.getSubscription(
            subscriptionId,
          );
        } else {
          response = await SubscriptionController.getCurrentSubscription(
            pageId,
            bySubscription,
          );
        }

        dispatch(this.unbusy());
        dispatch(this.setCurrentSubscriptionActionCreator(pageId, response));
        return response;
      } catch (e) {
        return subscriptionErrorHandler(dispatch)(e);
      }
    };
  }

  getCurrentSubscriptionCancels(subscriptionId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy());
        const response =
          await SubscriptionController.getCurrentSubscriptionCancels(
            subscriptionId,
          );
        dispatch(this.unbusy());

        // Note: BE returns a JSON string with an array of cancel objects,
        // but we currently do not impelemnt any logic that uses this data.
        // We are just interested to see if the array is empty or not for the current logc.

        const hasCancels = response?.cancels
          ? !!JSON.parse(response.cancels)?.length
          : false;
        return hasCancels;
      } catch (e) {
        return subscriptionErrorHandler(dispatch)(e);
      }
    };
  }

  getCreditsForFuturePayments(
    companyId: number,
    planCode: string,
    subscriptionId: number,
    isUpgrade: boolean,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy());
        const response =
          await SubscriptionController.getCreditsForFuturePayments({
            companyId,
            planCode,
            isUpgrade,
            subscriptionId,
          });
        dispatch(this.setCreditsForFuturePaymentsActionCreator(response));
        return response;
      } catch (e) {
        return subscriptionErrorHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  submitSeatsNumber(
    subscriptionId: number,
    extraUserSeats: number,
    companyId: number,
    facebookPageId: number,
    isBilling2023Subscription?: boolean,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy());
        const response = await SubscriptionController.submitSeatsNumber({
          subscriptionId,
          extraUserSeats,
        });
        dispatch(this.unbusy());
        dispatch(this.getAccountData(companyId));
        dispatch(
          this.getCurrentSubscription(
            facebookPageId,
            false,
            subscriptionId,
            isBilling2023Subscription,
          ),
        );

        return response;
      } catch (error) {
        const responseErrorBody = await getErrorJson(error);
        // Permissions error
        if (
          error instanceof Response &&
          responseErrorBody.errors.length &&
          responseErrorBody.errors[0] ===
            'Attempt to set less user seats than already in use'
        ) {
          dispatch(
            modalActions.openGeneralTextModal({
              header: 'User removal required',
              message:
                'Please remove users from Users page to be able to remove seats',
            }),
          );
          return;
        }
        return subscriptionErrorHandler(dispatch)(error);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  reedemPromocode(code: string, fbPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.busy());
        const response = await SubscriptionController.reedemPromocode(
          code,
          fbPageId,
        );
        return response;
      } catch (e) {
        if (e instanceof Response && e.status === UNPROCESSABLE_ENTITY) {
          throw e;
        }
        return subscriptionErrorHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }
}

const subscriptionCRUDActions = new SubscriptionCRUDActions();

export const useSubscriptionCRUDActions = getActionHookBuilder(
  subscriptionCRUDActions,
  [
    'getCurrentSubscription',
    'getCurrentSubscriptionCancels',
    'getPlans',
    'getShopifyPlans',
    'getPlanInfo',
    'getCouponData',
    'createProSubscription',
    'addExtraPagesToTeam',
    'getAccountData',
    'sendOveragesEnabled',
    'updateAllotment',
    'downgrade',
    'update',
    'upgrade',
    'activate',
    'getPriceForNextLevel',
    'submitSeatsNumber',
    'getPlan',
    'getSubscription',
    'reedemPromocode',
  ],
);

export default subscriptionCRUDActions;
